'use client';

import { useMemo, useState } from 'react';
import {
  DEFAULT_GLITCH_STATE,
  glitchCss,
  glitchHtml,
  glitchSnippet,
  type GlitchState,
} from '@/lib/browser-cssanim-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

const PREVIEW_PREFIX = 'cleanor-glitch-preview';

export default function BrowserCssTextGlitchGenerator() {
  const [state, setState] = useState<GlitchState>(DEFAULT_GLITCH_STATE);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const output = useMemo(() => glitchSnippet(state), [state]);
  const previewCss = useMemo(() => glitchCss(state, PREVIEW_PREFIX), [state]);
  const previewHtml = useMemo(() => glitchHtml(state, PREVIEW_PREFIX), [state]);

  function update<K extends keyof GlitchState>(key: K, value: GlitchState[K]) {
    setState((prev) => ({ ...prev, [key]: value }));
  }

  async function handleCopy() {
    try {
      await copyText(output);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Glitch text</span>
        <h2 className="tool-live-title">CSS text glitch effect generator</h2>
        <p className="tool-live-subtitle">
          Type your text, pick the layer colors and intensity, preview the animated glitch, and copy
          the HTML + CSS.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Text</span>
              <input
                onChange={(e) => update('text', e.target.value)}
                placeholder="GLITCH"
                type="text"
                value={state.text}
              />
            </label>
            <label className="tool-live-field">
              <span>Base color</span>
              <input
                onChange={(e) => update('color', e.target.value)}
                type="color"
                value={state.color}
              />
            </label>
            <label className="tool-live-field">
              <span>Glitch layer A</span>
              <input
                onChange={(e) => update('glitchA', e.target.value)}
                type="color"
                value={state.glitchA}
              />
            </label>
            <label className="tool-live-field">
              <span>Glitch layer B</span>
              <input
                onChange={(e) => update('glitchB', e.target.value)}
                type="color"
                value={state.glitchB}
              />
            </label>
            <label className="tool-live-field">
              <span>Intensity: {state.intensity}</span>
              <input
                max={10}
                min={1}
                onChange={(e) => update('intensity', Number(e.target.value))}
                type="range"
                value={state.intensity}
              />
            </label>
          </div>

          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleCopy} type="button">
              Copy result
            </button>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <div
            className="tool-live-preview-frame"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 200,
              background: '#0f172a',
              borderRadius: 12,
              fontSize: 44,
            }}
          >
            <style>{previewCss}</style>
            <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>

          <label className="tool-live-field tool-live-field-full">
            <span>HTML + CSS</span>
            <textarea readOnly rows={14} value={output} />
          </label>
          <p className="tool-live-panel-note">Generated locally in your browser.</p>
        </section>
      </div>
    </div>
  );
}
